import { fetchAuthSession } from 'aws-amplify/auth';

export const STAGES = ['dev', 'alpha', 'beta', 'gamma', 'prod'] as const;
export type Stage = typeof STAGES[number];

const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1']);
const PROD_HOSTNAMES = new Set([
  'console.keystone.device.finance.amazon.dev',
  'iam-console.keystone.device.finance.amazon.dev',
]);

export function resolveStage(hostname: string): Stage {
  if (LOCAL_HOSTNAMES.has(hostname)) return 'dev';
  if (PROD_HOSTNAMES.has(hostname)) return 'prod';
  if (hostname.includes('beta')) return 'beta';
  if (hostname.includes('gamma')) return 'gamma';
  if (hostname.includes('prod')) return 'prod';
  return 'alpha';
}

export function getStageUrl(stage: Stage): string {
  // Local dev has no backend of its own; it talks to the alpha API.
  const apiStage = stage === 'dev' ? 'alpha' : stage;
  // Prod has no stage label in its API hostname.
  if (apiStage === 'prod') return 'https://iam.keystone.device.finance.amazon.dev/iam/ui';
  return `https://iam.${apiStage}.keystone.device.finance.amazon.dev/iam/ui`;
}

const CURRENT_STAGE = resolveStage(window.location.hostname);
const BASE_URL = getStageUrl(CURRENT_STAGE);

export function getStage(): Stage {
  return CURRENT_STAGE;
}

export function getBaseUrl(): string {
  return BASE_URL;
}

export class SessionExpiredError extends Error {
  constructor() {
    super('Session expired — please sign in again');
    this.name = 'SessionExpiredError';
  }
}

async function getAuthToken(): Promise<string> {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    if (!token) throw new SessionExpiredError();
    return token;
  } catch (err) {
    if (err instanceof SessionExpiredError) throw err;
    throw new SessionExpiredError();
  }
}

export async function api(method: string, path: string, body?: unknown) {
  const url = `${BASE_URL}${path}`;
  const token = await getAuthToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };

  const res = await fetch(url, {
    method,
    headers,
    ...(body ? { body: JSON.stringify(body) } : {}),
  });

  if (res.status === 401) throw new SessionExpiredError();

  const data = await res.json().catch(() => null);
  // Keystone errors look like { error: 'DOES_NOT_EXIST', reason: "User group name 'x' does not exist" }.
  if (!res.ok) throw new Error(data?.reason || data?.message || data?.error || `HTTP ${res.status}`);
  return data;
}
